import classNames from "classnames";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { TableUI } from "components/design/TableUI";
import { FileIcon } from "components/design/FileIcon";
import { DocumentTagMultiSelect } from "components/DocumentTags/DocumentTagMultiSelect";
import { PreviewBody } from "components/PreviewModal";
import { MultiFiles } from "components/icons/MultiFiles";
import ToolTip from "components/design/toolTip";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  useConfirmDocumentTagsMutation,
  useGetDocumentTagReviewQuery,
} from "store/apis/documentTagReview";
import {
  useGetFolderOrFileQuery,
  useGetPreviewUrlQuery,
} from "store/apis/previewUrl";
import { closeDocumentTagReviewModals } from "store/slices/documentTagReviewModal";
import { RootState } from "store/store";
import { DocumentTagReviewDocument } from "types/Models/documentTagReview";
import { BackendError } from "types/utils/error";

const columnHelper = createColumnHelper<DocumentTagReviewDocument>();

const PreviewPane = ({
  groupId,
  documentId,
}: {
  groupId: string;
  documentId: string;
}) => {
  const { data: file, ...fileState } = useGetFolderOrFileQuery(
    { groupId, fileId: documentId },
    { skip: !groupId || !documentId }
  );

  const { data, ...previewState } = useGetPreviewUrlQuery(
    { groupId, fileId: file?.uuid },
    { skip: !file?.uuid, refetchOnMountOrArgChange: true }
  );

  const isLoading = fileState.isLoading || previewState.isLoading;
  const isSuccess = fileState.isSuccess && previewState.isSuccess;
  const isError = fileState.isError || previewState.isError;

  return (
    <div className="t-flex t-flex-col t-h-full">
      <div className="t-flex t-items-center t-gap-1.5 t-px-5 t-py-3 t-border-0 t-border-b t-border-solid t-border-neutral-0">
        <FileIcon fileType={file?.file_type} size="16" />
        <ToolTip text={file?.name}>
          <span className="t-truncate t-text-body-sm t-text-text-60">
            {file?.name}
          </span>
        </ToolTip>
      </div>
      <div className="t-flex-1 t-overflow-y-auto t-overflow-x-hidden t-px-5 t-pt-5">
        <Async.Root
          isLoading={isLoading}
          isSuccess={isSuccess}
          isError={isError}
          isEmpty={false}
        >
          <Async.ErrorHandler>
            <div className="t-flex t-items-center t-justify-center t-h-full t-text-body-sm t-text-text-30">
              Some issue opening the document, please try again.
            </div>
          </Async.ErrorHandler>
          <Async.Success>
            <PreviewBody
              file={file}
              preview_url={data?.preview_url!}
              fullWidth
            />
          </Async.Success>
        </Async.Root>
      </div>
    </div>
  );
};

const EmptyPreviewState = () => (
  <div className="t-flex t-flex-col t-items-center t-justify-center t-h-full t-gap-2 t-text-text-30">
    <MultiFiles size="40" />
    <span className="t-text-subtext-sm">No preview</span>
    <span className="t-text-body-sm">
      Click to select a document to preview
    </span>
  </div>
);

export const DocumentTagReviewModal = () => {
  const dispatch = useDispatch();
  const { alertToast } = useToast();
  const [manualSelection, setManualSelection] = useState<{
    messageId: string;
    docId: string | null;
  } | null>(null);

  const isOpen = useSelector(
    (state: RootState) => state.documentTagReviewModal.isTagReviewModalOpen
  );
  const groupId = useSelector(
    (state: RootState) => state.documentTagReviewModal.groupId
  );
  const messageId = useSelector(
    (state: RootState) => state.documentTagReviewModal.messageId
  );

  const [tagsByDocumentId, setTagsByDocumentId] = useState<
    Record<string, string[]>
  >({});

  const {
    data,
    isLoading: isDocumentsLoading,
    isFetching: isDocumentsFetching,
    isSuccess,
    isError: isDocumentsError,
  } = useGetDocumentTagReviewQuery(
    { groupId: groupId!, messageId: messageId! },
    { skip: !isOpen || !groupId || !messageId }
  );

  const [confirmDocumentTags, { isLoading: isConfirming }] =
    useConfirmDocumentTagsMutation();

  const documents = data?.documents || [];

  const hasManualSelectionForThisMessage =
    manualSelection?.messageId === messageId &&
    documents.some((document) => document.uuid === manualSelection?.docId);

  const selectedDocId = hasManualSelectionForThisMessage
    ? manualSelection!.docId
    : documents[0]?.uuid ?? null;

  const setSelectedDocId = (docId: string | null) => {
    setManualSelection({ messageId: messageId!, docId });
  };

  const getSelectedTagUuids = (documentId: string) =>
    tagsByDocumentId[documentId] ??
    documents
      .find((document) => document.uuid === documentId)
      ?.tags.map((tag) => tag.uuid) ??
    [];

  const onTagsChange = (documentId: string, tagUuids: string[]) => {
    setTagsByDocumentId((prev) => ({ ...prev, [documentId]: tagUuids }));
  };

  const onClose = () => {
    setManualSelection(null);
    setTagsByDocumentId({});
    dispatch(closeDocumentTagReviewModals());
  };

  const onConfirm = async () => {
    try {
      await confirmDocumentTags({
        groupId: groupId!,
        messageId: messageId!,
        documents: documents.map((document) => ({
          document_id: document.uuid,
          tag_ids: getSelectedTagUuids(document.uuid),
        })),
      }).unwrap();
      onClose();
    } catch (err) {
      alertToast({
        message:
          (err as BackendError)?.data?.error?.message ?? "Something went wrong",
      });
    }
  };

  const columns = [
    columnHelper.display({
      id: "document",
      header: "Document",
      size: 50,
      cell: (info) => (
        <div className="t-flex t-items-center t-gap-1.5 t-min-w-0">
          <FileIcon fileType={info.row.original.file_type} size="16" />
          <ToolTip text={info.row.original.name}>
            <span className="t-truncate t-text-body-sm t-text-text-60">
              {info.row.original.name}
            </span>
          </ToolTip>
        </div>
      ),
    }),
    columnHelper.display({
      id: "tags",
      header: "Tags",
      size: 50,
      cell: (info) => (
        <div onClick={(e) => e.stopPropagation()}>
          <DocumentTagMultiSelect
            groupId={groupId!}
            selectedUuids={getSelectedTagUuids(info.row.original.uuid)}
            onChange={(tagUuids) =>
              onTagsChange(info.row.original.uuid, tagUuids)
            }
          />
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: documents,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.uuid,
    state: {
      rowSelection: selectedDocId ? { [selectedDocId]: true } : {},
    },
    defaultColumn: { size: 10, minSize: 5, maxSize: 100 },
  });

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content size="xxl">
        <Modal.Header>
          <Modal.Title>Document tags</Modal.Title>
          <Modal.Close />
        </Modal.Header>

        <Modal.Body className="!t-p-0 t-flex t-h-[34rem]" asChild>
          <div
            className={classNames("t-flex t-h-[34rem]", {
              "t-items-center t-justify-center":
                isDocumentsLoading || isDocumentsFetching,
            })}
          >
            <Async.Root
              isLoading={isDocumentsLoading || isDocumentsFetching}
              isSuccess={isSuccess}
              isError={isDocumentsError}
              isEmpty={documents.length === 0}
            >
              <Async.Empty>
                <div className="t-flex t-items-center t-justify-center t-w-full t-text-body-sm t-text-text-30">
                  No documents to review
                </div>
              </Async.Empty>
              <Async.Success>
                <div className="t-w-1/2 t-border-0 t-border-r t-border-solid t-border-neutral-0 t-overflow-auto t-p-5">
                  <p className="t-text-body-sm t-text-text-30 t-mb-5 t-mt-0">
                    Please review the documents tagged and make any necessary
                    changes.
                  </p>
                  <TableUI
                    table={table}
                    verticalAlign="middle"
                    layout="fixed"
                    onRowClick={(row) => setSelectedDocId(row.original.uuid)}
                  />
                </div>
                <div className="t-w-1/2">
                  {selectedDocId ? (
                    <PreviewPane
                      groupId={groupId!}
                      documentId={selectedDocId}
                    />
                  ) : (
                    <EmptyPreviewState />
                  )}
                </div>
              </Async.Success>
            </Async.Root>
          </div>
        </Modal.Body>

        <Modal.FooterButtonGroup>
          <Button customType="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            customType="primary"
            onClick={onConfirm}
            isLoading={isConfirming}
          >
            Confirm
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
